import { BatteryChemistry, type BatteryInput, type SolveMode, type CalculationMode } from '@brt/shared-types'
import { getChemistryPreset } from '@brt/shared-logic'
import type { AppStore } from '../useAppStore.ts'

export interface BatterySlice {
  battery: BatteryInput
  solveMode: SolveMode
  calculationMode: CalculationMode
  deviceMinVoltage: number | null
  updateBattery: <K extends keyof BatteryInput>(field: K, value: BatteryInput[K]) => void
  setChemistry: (chemistry: BatteryChemistry) => void
  setSolveMode: (mode: SolveMode) => void
  updateDeviceMinVoltage: (v: number | null) => void
}

const DEFAULT_BATTERY: BatteryInput = {
  chemistry: BatteryChemistry.LI_FEPO4,
  capacityValue: 100,
  capacityUnit: 'AH',
  voltage: 12.8,
  dod: 0.80,
  stateOfCharge: 1.0,
  stateOfHealth: 1.0,
  temperatureC: 25,
  cutoffVoltage: 2.5,
  ageCycles: 0,
  storageDays: 0,
}

type Set = (fn: (state: AppStore) => Partial<AppStore>) => void
type Get = () => AppStore

export function createBatterySlice(set: Set, _get: Get): BatterySlice {
  return {
    battery: { ...DEFAULT_BATTERY },
    solveMode: 'RUNTIME',
    calculationMode: 'CURRENT_BASED',
    deviceMinVoltage: null,

    updateBattery: (field, value) =>
      set((state) => ({
        battery: { ...state.battery, [field]: value },
      })),

    setChemistry: (chemistry) => {
      const preset = getChemistryPreset(chemistry)
      set((state) => ({
        battery: {
          ...state.battery,
          chemistry,
          cutoffVoltage: preset.cutoffVoltage,
          dod: preset.defaultDoD,
        },
      }))
    },

    setSolveMode: (mode) => set(() => ({ solveMode: mode })),

    updateDeviceMinVoltage: (v) => set(() => ({ deviceMinVoltage: v })),
  }
}

export { DEFAULT_BATTERY }
